// src/features/userManagement/components/EditUserModal.jsx

import React, { useState, useEffect } from 'react';
import ScreenPermissionSelector from './ScreenPermissionSelector';
import { listAllScreens, getUserScreens } from '../services/screenService';

export default function EditUserModal({ open, user, organizationId, submitting, onClose, onSubmit }) {
  const [form, setForm] = useState({ email: '', firstname: '', lastname: '', user_type: '' });
  const [allScreens, setAllScreens] = useState([]);
  const [screensLoading, setScreensLoading] = useState(false);
  const [screenMap, setScreenMap] = useState({}); // screenId -> 'READ' | 'READ_WRITE'

  useEffect(() => {
    if (user) {
      setForm({
        email: user.email ?? '',
        firstname: user.firstname ?? '',
        lastname: user.lastname ?? '',
        user_type: user.user_type ?? '',
      });
    }
  }, [user]);

  // Load the screen catalog + the user's current grants when the modal opens.
  useEffect(() => {
    if (!open || !user) return;
    let cancelled = false;
    setScreensLoading(true);
    Promise.all([
      listAllScreens().catch(() => []),
      organizationId ? getUserScreens(organizationId, user.client_id).catch(() => []) : Promise.resolve([]),
    ])
      .then(([screens, grants]) => {
        if (cancelled) return;
        setAllScreens(screens || []);
        const map = {};
        (grants || []).forEach((g) => { map[g.screen_id] = g.access_level; });
        setScreenMap(map);
      })
      .finally(() => { if (!cancelled) setScreensLoading(false); });
    return () => { cancelled = true; };
  }, [open, user, organizationId]);

  if (!open || !user) return null;

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const screens = Object.entries(screenMap).map(([screen_id, access_level]) => ({
      screen_id: Number(screen_id),
      access_level,
    }));
    onSubmit(user.client_id, form, screens);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-base font-semibold text-gray-900">Edit User - {user.username}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
          <div className="px-6 py-5 space-y-4 overflow-y-auto">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={e => set('email', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={form.firstname}
                  onChange={e => set('firstname', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={form.lastname}
                  onChange={e => set('lastname', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Type</label>
              <select
                value={form.user_type}
                onChange={e => set('user_type', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="ORG_USER">Org User</option>
                <option value="ORG_ADMIN">Org Admin</option>
              </select>
            </div>

            {/* Screen permissions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Screen Permissions</label>
              <p className="text-xs text-gray-400 mb-2">
                Org Admins see every screen regardless of this selection. For Org Users, only checked screens are visible.
              </p>
              <ScreenPermissionSelector
                allScreens={allScreens}
                value={screenMap}
                onChange={setScreenMap}
                loading={screensLoading}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
